/**
 * DOM utility functions for common operations like waiting for elements.
 */

/**
 * Wait for an element to appear in the DOM using MutationObserver.
 * Returns a promise that resolves with the element when found, or null on timeout.
 *
 * @param selector - CSS selector for the element to wait for
 * @param container - Container element to observe (defaults to document.body)
 * @param timeout - Maximum time to wait in milliseconds (defaults to 2000ms)
 * @returns Promise that resolves with the element or null if not found within timeout
 *
 * @example
 * // Wait for a modal to appear
 * const modal = await waitForElement('.modal-content', document.body);
 * if (modal) {
 *   modal.focus();
 * }
 */
export const waitForElement = (
  selector: string,
  container: HTMLElement = document.body,
  timeout = 2000,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    // Check if element already exists
    const existing = container.querySelector(selector) as HTMLElement | null;
    if (existing) {
      resolve(existing);
      return;
    }

    // Set up observer to watch for the element
    const observer = new MutationObserver(() => {
      const element = container.querySelector(selector) as HTMLElement | null;
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(container, { childList: true, subtree: true });

    // Timeout fallback
    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeout);
  });
};
